<template>
  <div class="online-heartbeat-page">
    <section class="online-toolbar">
      <div>
        <p class="online-toolbar__eyebrow">Steam Auth Gateway</p>
        <h2>在线心跳</h2>
        <p class="online-toolbar__copy">WebSocket 实时订阅 OTOOL 在线用户、客户端环境和授权控制状态。</p>
      </div>
      <div class="online-status">
        <t-tag :theme="statusTheme" variant="light">{{ statusText }}</t-tag>
        <span>{{ lastRefreshText }}</span>
      </div>
    </section>

    <section class="online-summary">
      <div class="summary-item">
        <span>在线用户</span>
        <strong>{{ totalSessionCount }}</strong>
      </div>
      <div class="summary-item">
        <span>连接状态</span>
        <strong>{{ statusText }}</strong>
      </div>
      <div class="summary-item">
        <span>服务时间</span>
        <strong>{{ serverTimeText }}</strong>
      </div>
    </section>

    <t-alert
      v-if="missingToken"
      theme="warning"
      message="缺少 VUE_APP_NODE_ADMIN_TOKEN，无法连接 Node 管理 WebSocket。请在构建环境变量中配置。"
      close
    />

    <t-table
      class="online-table"
      row-key="steamId64"
      :data="visibleSessions"
      :columns="columns"
      :loading="connecting"
      :pagination="pagination"
      table-layout="fixed"
      @page-change="onPageChange"
      size="medium"
    />
  </div>
</template>

<script>
import { computed, h, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { Button, MessagePlugin, Space, Tag } from 'tdesign-vue-next';

const DEFAULT_NODE_WS_URL = 'wss://aka-cloud.china.gameplanet.vip/ws/admin/sessions';
const NODE_ADMIN_WS_URL = process.env.VUE_APP_NODE_ADMIN_WS_URL || DEFAULT_NODE_WS_URL;
const NODE_ADMIN_TOKEN = process.env.VUE_APP_NODE_ADMIN_TOKEN || '';
const WS_OPEN = 1;

const safeText = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  return String(value);
};

const formatTime = (value) => {
  const numberValue = Number(value || 0);
  if (!numberValue) return '-';
  return new Date(numberValue * 1000).toLocaleString();
};

const formatDuration = (value) => {
  const numberValue = Number(value || 0);
  if (!numberValue) return '-';
  const seconds = Math.max(0, Math.floor(Date.now() / 1000 - numberValue));
  if (seconds < 60) return `${seconds} 秒前`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟前`;
  return `${Math.floor(seconds / 3600)} 小时前`;
};

export default {
  name: 'OnlineHeartbeat',
  setup() {
    const store = useStore();
    const sessions = ref([]);
    const status = ref('idle');
    const lastRefreshAt = ref(0);
    const serverTime = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const missingToken = ref(!NODE_ADMIN_TOKEN);
    let socket = null;
    let reconnectTimer = null;
    let pingTimer = null;

    const isSuperAdmin = computed(() => String(store.state.user.owner) === '0');
    const visibleSessions = computed(() => (isSuperAdmin.value ? sessions.value : getLatestSessions(sessions.value)));
    const totalSessionCount = computed(() => sessions.value.length);
    const connecting = computed(() => status.value === 'connecting');
    const statusText = computed(() => {
      const map = {
        idle: '未连接',
        connecting: '连接中',
        connected: '已连接',
        closed: '已断开',
        error: '连接异常',
      };
      return map[status.value] || status.value;
    });
    const statusTheme = computed(() => {
      if (status.value === 'connected') return 'success';
      if (status.value === 'connecting') return 'primary';
      if (status.value === 'error') return 'danger';
      return 'warning';
    });
    const lastRefreshText = computed(() => {
      if (!lastRefreshAt.value) return '等待实时数据';
      return `最后更新 ${new Date(lastRefreshAt.value).toLocaleTimeString()}`;
    });
    const serverTimeText = computed(() => formatTime(serverTime.value));
    const pagination = computed(() => ({
      current: currentPage.value,
      pageSize: pageSize.value,
      total: visibleSessions.value.length,
      showJumper: true,
      showPageSize: true,
      pageSizeOptions: [10, 20, 50, 100],
    }));

    const onPageChange = ({ current, pageSize: nextPageSize }) => {
      currentPage.value = current || 1;
      pageSize.value = nextPageSize || 10;
    };

    const getLatestSessions = (list) =>
      [...list]
        .sort((a, b) => Number(b.lastSeenAt || 0) - Number(a.lastSeenAt || 0))
        .slice(0, 10);

    const sendJson = (payload) => {
      if (socket?.readyState === WS_OPEN) {
        socket.send(JSON.stringify(payload));
        return true;
      }
      return false;
    };

    const scheduleReconnect = () => {
      if (reconnectTimer || missingToken.value) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connectSocket();
      }, 3000);
    };

    const stopTimers = () => {
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (pingTimer) window.clearInterval(pingTimer);
      reconnectTimer = null;
      pingTimer = null;
    };

    const closeSocket = () => {
      stopTimers();
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
      }
      socket = null;
    };

    const handleMessage = (event) => {
      let message;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }

      if (message.type === 'admin_hello_ack') {
        serverTime.value = message.serverTime || 0;
        return;
      }

      if (message.type === 'session_snapshot') {
        sessions.value = message.sessions || [];
        currentPage.value = 1;
        serverTime.value = message.serverTime || 0;
        lastRefreshAt.value = Date.now();
        return;
      }

      if (message.type === 'command_ack') {
        const affectedRows = message.grantResult?.affectedRows;
        if (message.command?.type === 'revoke_all' && affectedRows !== undefined) {
          MessagePlugin.success(`已封禁 ${affectedRows} 条授权记录`);
        } else {
          MessagePlugin.success('命令已下发');
        }
        return;
      }

      if (message.type === 'error') {
        MessagePlugin.error(message.message || 'WebSocket 返回错误');
      }
    };

    function connectSocket() {
      if (missingToken.value) return;
      closeSocket();
      status.value = 'connecting';
      socket = new WebSocket(NODE_ADMIN_WS_URL);

      socket.onopen = () => {
        status.value = 'connected';
        sendJson({ type: 'admin_hello', token: NODE_ADMIN_TOKEN });
        pingTimer = window.setInterval(() => sendJson({ type: 'ping' }), 25000);
      };
      socket.onmessage = handleMessage;
      socket.onerror = () => {
        status.value = 'error';
      };
      socket.onclose = () => {
        if (status.value !== 'error') status.value = 'closed';
        socket = null;
        stopTimers();
        scheduleReconnect();
      };
    }

    const runCommand = (row, action) => {
      if (!isSuperAdmin.value) return;
      if (!row?.steamId64) return;
      const actionMap = {
        refresh: { action: 'refresh_auth', message: '刷新授权命令已发送' },
        revoke: { action: 'revoke_all', message: '封禁授权命令已发送' },
        kick: { action: 'kick', message: '踢下线命令已发送' },
      };
      const current = actionMap[action];
      if (!current) return;
      const ok = sendJson({
        type: 'command',
        action: current.action,
        steamId64: row.steamId64,
        reason: `admin_${action}`,
      });
      if (ok) MessagePlugin.success(current.message);
      else MessagePlugin.error('WebSocket 未连接，命令未发送');
    };

    const columns = computed(() => {
      const baseColumns = [
      {
        colKey: 'steamId64',
        title: 'SteamID64',
        width: 180,
        fixed: 'left',
        cell: (render, { row }) => h('strong', safeText(row.steamId64)),
      },
      {
        colKey: 'lastSeenAt',
        title: '最后心跳',
        width: 150,
        sorter: (a, b) => Number(a.lastSeenAt || 0) - Number(b.lastSeenAt || 0),
        cell: (render, { row }) => h(Tag, { theme: 'success', variant: 'light' }, () => formatDuration(row.lastSeenAt)),
      },
      { colKey: 'clientVersion', title: '客户端版本', width: 190, cell: (render, { row }) => safeText(row.clientVersion) },
      { colKey: 'steamVersionHash', title: 'Steam HASH', width: 220, ellipsis: true, cell: (render, { row }) => safeText(row.steamVersionHash) },
      { colKey: 'systemMac', title: 'MAC', width: 170, cell: (render, { row }) => safeText(row.systemMac) },
      { colKey: 'systemName', title: '系统名称', width: 190, cell: (render, { row }) => safeText(row.systemName) },
      { colKey: 'systemVersion', title: '系统版本', width: 190, cell: (render, { row }) => safeText(row.systemVersion) },
      { colKey: 'deviceId', title: '设备ID', width: 180, ellipsis: true, cell: (render, { row }) => safeText(row.deviceId) },
      { colKey: 'systemInstallId', title: '安装ID', width: 180, ellipsis: true, cell: (render, { row }) => safeText(row.systemInstallId) },
      { colKey: 'systemInstallTime', title: '系统安装时间', width: 180, cell: (render, { row }) => formatTime(row.systemInstallTime) },
      { colKey: 'bootTime', title: '开机时间', width: 180, cell: (render, { row }) => formatTime(row.bootTime) },
      ];

      if (isSuperAdmin.value) {
        baseColumns.push({
        colKey: 'operation',
        title: '操作',
        width: 250,
        fixed: 'right',
        cell: (render, { row }) => h(Space, { size: 'small' }, () => [
          h(Button, { size: 'small', theme: 'primary', variant: 'outline', onClick: () => runCommand(row, 'refresh') }, () => '刷新授权'),
          h(Button, { size: 'small', theme: 'warning', variant: 'outline', onClick: () => runCommand(row, 'revoke') }, () => '封禁授权'),
          h(Button, { size: 'small', theme: 'danger', variant: 'outline', onClick: () => runCommand(row, 'kick') }, () => '踢下线'),
        ]),
        });
      }

      return baseColumns;
    });

    onMounted(connectSocket);
    onBeforeUnmount(closeSocket);

    return {
      columns,
      connecting,
      lastRefreshText,
      missingToken,
      onPageChange,
      pagination,
      serverTimeText,
      sessions,
      statusText,
      statusTheme,
      totalSessionCount,
      visibleSessions,
    };
  },
};
</script>

<style scoped>
.online-heartbeat-page {
  display: grid;
  gap: 16px;
}

.online-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px;
  border: 1px solid #e7edf5;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.online-toolbar__eyebrow {
  margin: 0 0 8px;
  color: #0052d9;
  font-size: 12px;
  font-weight: 700;
}

.online-toolbar h2 {
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
}

.online-toolbar__copy {
  margin: 8px 0 0;
  color: #667085;
}

.online-status {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #667085;
  white-space: nowrap;
}

.online-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 12px;
}

.summary-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
  padding: 16px;
  border: 1px solid #e7edf5;
  border-radius: 8px;
  background: #ffffff;
}

.summary-item span {
  color: #667085;
  font-size: 13px;
}

.summary-item strong {
  color: #111827;
  font-size: 22px;
}

.online-table {
  border: 1px solid #e7edf5;
  border-radius: 8px;
  background: #ffffff;
}

@media (max-width: 1100px) {
  .online-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .online-status {
    justify-content: space-between;
  }

  .online-summary {
    grid-template-columns: 1fr;
  }
}
</style>
